import { useState, useEffect } from 'react'
import type { VisibilityState } from '@tanstack/react-table'

/**
 * Custom hook for persisting column visibility to localStorage
 *
 * @param storageKey - Unique key for localStorage (e.g., 'accounts-column-visibility')
 * @param defaultVisibility - Default column visibility (columns not listed default to visible)
 * @returns [columnVisibility, setColumnVisibility]
 */
export function useColumnVisibility(
  storageKey: string,
  defaultVisibility: VisibilityState = {}
): [VisibilityState, React.Dispatch<React.SetStateAction<VisibilityState>>] {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return { ...defaultVisibility, ...JSON.parse(stored) }
      }
    } catch (error) {
      console.error('Error loading column visibility from localStorage:', error)
    }
    return defaultVisibility
  })

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnVisibility))
    } catch (error) {
      console.error('Error saving column visibility to localStorage:', error)
    }
  }, [columnVisibility, storageKey])

  return [columnVisibility, setColumnVisibility]
}
